/**
 * PURPOSE: Pantalla externa fullscreen del scoreboard local de Handball para TV por HDMI.
 * RESPONSIBILITIES:
 *  - Sí: ocupar 100% del viewport sin scroll y mostrar logos, abreviaturas, score gigante, reloj, TM y 2'.
 *  - Sí: consumir estado por BroadcastChannel desde la planilla abierta.
 *  - Sí: respetar el intercambio visual enviado desde la planilla y atender solicitud de fullscreen TV.
 *  - No: no llama backend, no registra eventos y no usa Redux.
 * COLLABORATORS:
 *  - hooks/useHandballScoreboardReceiver.js
 *  - components/scoreboard/HandballScoreboardTeamPanel.jsx
 * NOTES: La estructura visual se inspira en scoreboard de TV: logos arriba, reloj al centro y puntajes protagonistas abajo.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/Handball/components/scoreboard/HandballScoreboardView.jsx
 */

import React, { useMemo } from "react";
import { useHandballScoreboardReceiver } from "../../hooks/useHandballScoreboardReceiver.js";
import HandballScoreboardTeamPanel from "./HandballScoreboardTeamPanel.jsx";

// Pantalla de espera cuando la TV todavía no recibió snapshot de la planilla.
const WaitingScreen = ({ status }) => {
  const unsupported = status === "unsupported";
  const planillaClosed = status === "planilla_closed";

  return (
    <main className="fixed inset-0 flex h-[100dvh] w-screen items-center justify-center overflow-hidden bg-slate-950 text-white">
      <div className="mx-auto max-w-3xl rounded-[2rem] border border-white/10 bg-white/[0.06] p-10 text-center shadow-2xl backdrop-blur">
        <h1 className="text-4xl font-black tracking-tight md:text-5xl">
          {unsupported
            ? "BroadcastChannel no disponible"
            : planillaClosed
              ? "Planilla cerrada"
              : "Esperando sincronización del partido..."}
        </h1>
        <p className="mt-5 text-lg font-semibold text-slate-300">
          {unsupported
            ? "Abre esta pantalla en un navegador moderno compatible."
            : "Abre la planilla de Handball en esta misma laptop y presiona “TV”."}
        </p>
      </div>
    </main>
  );
};

// Convierte número de período a texto público, sutil y claro.
const periodLabel = (period) => {
  const p = Number(period) || 1;
  if (p === 1) return "Primer Periodo";
  if (p === 2) return "Segundo Periodo";
  if (p === 3) return "Primer Suplementario";
  if (p === 4) return "Segundo Suplementario";
  return `Periodo ${p}`;
};

// Bloque central del reloj. Solo el borde/glow cambia por estado; el texto se mantiene blanco.
const ClockPanel = ({ timeStr, running, period }) => {
  const stateClass = running
    ? "border-emerald-300 shadow-[0_0_42px_rgba(110,231,183,0.78)]"
    : "border-yellow-300 shadow-[0_0_42px_rgba(253,224,71,0.72)]";

  return (
    <section className={`flex h-full min-w-0 flex-col items-center justify-center rounded-[1.6rem] border-2 bg-white/[0.055] px-5 py-4 backdrop-blur ${stateClass}`}>
      <div className="tabular-nums text-[clamp(5.8rem,11vw,11rem)] font-black leading-none tracking-tighter text-white drop-shadow-2xl">
        {timeStr}
      </div>
      <div className="mt-3 rounded-lg border border-white/15 bg-black/25 px-3 py-1 text-base font-black uppercase leading-none tracking-[0.16em] text-white/72 xl:text-lg">
        {periodLabel(period)}
      </div>
    </section>
  );
};

// Cronómetro visual de TM con jerarquía alta para lectura rápida desde tribuna/mesa.
const TimeoutCountdownBadge = ({ countdown, align = "left" }) => {
  if (!countdown?.remainingLabel) return null;
  const isRight = align === "right";

  return (
    <div className={`absolute top-4 z-30 rounded-[1.2rem] border border-amber-200/55 bg-amber-400/22 px-5 py-2.5 text-[clamp(2.6rem,4.2vw,5rem)] font-black leading-none tabular-nums text-amber-50 shadow-[0_0_34px_rgba(251,191,36,0.28)] backdrop-blur ${isRight ? "right-6 text-right" : "left-6 text-left"}`}>
      <span className="mr-3 align-middle text-[0.42em] font-black uppercase tracking-[0.18em] text-amber-100/85">TM</span>
      {countdown.remainingLabel}
    </div>
  );
};

// Badge discreto de TM usados en el período; el countdown activo es el que tiene jerarquía alta.
const TeamTimeoutBadge = ({ timeoutState, align = "left" }) => {
  if (timeoutState?.periodAllowed === false) return null;
  const isRight = align === "right";

  return (
    <div className={`absolute bottom-4 z-20 rounded-full border border-cyan-300/28 bg-cyan-400/13 px-4 py-1.5 text-base font-black uppercase tracking-tight text-cyan-100 backdrop-blur xl:text-xl ${isRight ? "right-6" : "left-6"}`}>
      TM {timeoutState?.usedInPeriod ?? 0}/2
    </div>
  );
};

// Riel vertical de exclusiones 2'. En TV va pegado al lado visual del equipo, no mezclado con el score.
const ExclusionRail = ({ exclusions = [], align = "left" }) => {
  const visibleExclusions = exclusions || [];
  if (!visibleExclusions.length) return null;

  const isRight = align === "right";

  return (
    <div
      className={`absolute top-1/2 z-30 grid max-h-[88%] -translate-y-1/2 grid-flow-col grid-rows-6 gap-1.5 ${
        isRight ? "right-3 justify-items-end" : "left-3 justify-items-start"
      }`}
    >
      {visibleExclusions.map((item) => {
        const isActive = Boolean(item.active && item.remainingLabel);

        return (
          <div
            key={`${item.idaccreditation || item.idmatchevent}-${item.jersey}`}
            className={[
              "min-w-[7rem] rounded-xl border px-2 py-1.5 text-center text-[clamp(1.2rem,1.55vw,1.8rem)] font-black leading-none backdrop-blur",
              isActive
                ? "border-orange-300/80 bg-orange-500/35 text-orange-50 shadow-[0_0_24px_rgba(251,146,60,0.32)]"
                : "border-orange-200/30 bg-slate-950/55 text-orange-100/85 shadow-lg",
            ].join(" ")}
            aria-label={`Jugador ${item.jersey}, ${item.exclusionCount || 1} exclusiones de 2 minutos${isActive ? ", exclusión en curso" : ""}`}
          >
            <div
              className="flex min-h-2.5 items-center justify-center gap-1"
              title={`${item.exclusionCount || 1} exclusiones de 2 minutos`}
            >
              {Array.from({ length: Math.max(1, Number(item.exclusionCount) || 1) }).map(
                (_, index) => (
                  <span
                    key={index}
                    className={`h-2 w-2 rounded-full border border-orange-100/70 ${
                      isActive
                        ? "bg-orange-300 shadow-[0_0_7px_rgba(253,186,116,0.8)]"
                        : "bg-orange-400/70"
                    }`}
                  />
                )
              )}
            </div>
            <div className="mt-1 flex items-center justify-center gap-1.5 tabular-nums">
              <span>#{item.jersey}</span>
              <span className={`text-[0.62em] ${isActive ? "text-orange-100" : "text-orange-200/65"}`}>
                {isActive ? item.remainingLabel : "2'"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Bloque inferior del puntaje gigante por equipo.
const ScorePanel = ({ score, timeoutState, timeoutCountdown, exclusions, align = "left" }) => {
  const isRight = align === "right";
  const scorePadding = isRight ? "pr-[9vw]" : "pl-[9vw]";

  return (
    <section className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-[1.6rem] border border-white/10 bg-white/[0.035] shadow-2xl">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08),transparent_56%)]" />
      <TimeoutCountdownBadge countdown={timeoutCountdown} align={align} />
      <ExclusionRail exclusions={exclusions} align={align} />
      <div
        className={`relative tabular-nums text-[clamp(13rem,33vw,31rem)] font-black leading-none tracking-tighter text-white drop-shadow-[0_20px_45px_rgba(0,0,0,0.65)] ${scorePadding}`}
      >
        {Number.isFinite(Number(score)) ? Number(score) : 0}
      </div>
      <TeamTimeoutBadge timeoutState={timeoutState} align={align} />
    </section>
  );
};

// Overlay de fallback cuando el navegador bloquea fullscreen remoto por falta de gesto directo en la ventana TV.
const FullscreenPrompt = ({ onRequest, onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-8 text-white backdrop-blur-sm">
    <div className="max-w-2xl rounded-[2rem] border border-white/15 bg-slate-950/92 p-8 text-center shadow-2xl">
      <h2 className="text-4xl font-black tracking-tight">Pantalla completa TV</h2>
      <p className="mt-4 text-lg font-semibold text-slate-200">
        El navegador necesita un click directo en esta ventana para activar pantalla completa.
      </p>
      <div className="mt-7 flex justify-center gap-3">
        <button
          type="button"
          onClick={onRequest}
          className="rounded-2xl bg-cyan-500 px-7 py-3 text-lg font-black text-white shadow-lg transition hover:bg-cyan-600"
        >
          Poner en pantalla completa
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-2xl border border-white/15 bg-white/10 px-6 py-3 text-lg font-black text-white/85 transition hover:bg-white/15"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
);

// Agrupa datos reales home/away y aplica swap visual local del scoreboard.
function buildVisualScoreboardSides(state, swapped) {
  const home = {
    team: state?.homeTeam,
    score: state?.score?.home ?? 0,
    timeoutState: state?.timeouts?.home,
    timeoutCountdown: state?.timeoutCountdown?.home,
    exclusions: state?.exclusions?.home || [],
  };

  const away = {
    team: state?.awayTeam,
    score: state?.score?.away ?? 0,
    timeoutState: state?.timeouts?.away,
    timeoutCountdown: state?.timeoutCountdown?.away,
    exclusions: state?.exclusions?.away || [],
  };

  return swapped ? { left: away, right: home } : { left: home, right: away };
}

// Vista principal de scoreboard: logos/abreviaturas arriba, reloj al centro y puntajes gigantes abajo.
const HandballScoreboardView = () => {
  const {
    state,
    status,
    fullscreenPrompt,
    scoreboardZoom,
    requestFullscreen,
    clearFullscreenPrompt,
  } = useHandballScoreboardReceiver();
  const scoreboardSidesSwapped = Boolean(state?.visual?.sidesSwapped);

  const visual = useMemo(
    () => buildVisualScoreboardSides(state, scoreboardSidesSwapped),
    [state, scoreboardSidesSwapped]
  );

  if (!state) return <WaitingScreen status={status} />;

  const running = Boolean(state.clock?.running);
  const timeStr = state.clock?.timeStr || "00:00";

  return (
    <main className="fixed inset-0 h-[100dvh] w-screen overflow-hidden bg-[radial-gradient(circle_at_top,#1e3a8a_0%,#020617_48%,#000_100%)] text-white">
      {fullscreenPrompt ? (
        <FullscreenPrompt onRequest={requestFullscreen} onDismiss={clearFullscreenPrompt} />
      ) : null}

      <div className="flex h-full w-full items-center justify-center overflow-hidden">
        <div
          className="h-full w-full transition-transform duration-200 ease-out"
          style={{ transform: `scale(${scoreboardZoom || 1})`, transformOrigin: "center center", willChange: "transform" }}
        >
          <div className="grid h-full w-full grid-rows-[27dvh_1fr] gap-[clamp(0.75rem,1.15vw,1.25rem)] p-[clamp(0.9rem,1.35vw,1.55rem)]">
            <section className="grid min-h-0 grid-cols-[1fr_1fr_1fr] gap-[clamp(0.75rem,1.15vw,1.25rem)]">
              <HandballScoreboardTeamPanel team={visual.left.team} align="left" />
              <ClockPanel timeStr={timeStr} running={running} period={state.period} />
              <HandballScoreboardTeamPanel team={visual.right.team} align="right" />
            </section>

            <section className="grid min-h-0 grid-cols-2 gap-[clamp(0.75rem,1.15vw,1.25rem)]">
              <ScorePanel
                score={visual.left.score}
                timeoutState={visual.left.timeoutState}
                timeoutCountdown={visual.left.timeoutCountdown}
                exclusions={visual.left.exclusions}
                align="left"
              />
              <ScorePanel
                score={visual.right.score}
                timeoutState={visual.right.timeoutState}
                timeoutCountdown={visual.right.timeoutCountdown}
                exclusions={visual.right.exclusions}
                align="right"
              />
            </section>
          </div>
        </div>
      </div>
    </main>
  );
};

export default HandballScoreboardView;
